import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  X,
  Check,
  Sparkles,
  Copy,
  RotateCcw,
  Maximize2,
  Minimize2,
  Type,
} from "lucide-react";

interface PromptEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onChange: (value: string) => void;
  title?: string;
  description?: string;
  placeholder?: string;
}

export const PromptEditorModal: React.FC<PromptEditorModalProps> = ({
  isOpen,
  onClose,
  value,
  onChange,
  title = "Edit Prompt",
  description = "Write your instructions for the AI model",
  placeholder = "Enter your prompt here...",
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      setLocalValue(value);
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isOpen, value]);

  const handleSave = () => {
    onChange(localValue);
    onClose();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(localValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReset = () => {
    setLocalValue(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
    if (e.key === "Tab") {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (!textarea) return;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue =
        localValue.substring(0, start) + "  " + localValue.substring(end);
      setLocalValue(newValue);
      setTimeout(() => {
        textarea.setSelectionRange(start + 2, start + 2);
      }, 0);
    }
  };

  const wordCount = localValue.trim()
    ? localValue.trim().split(/\s+/).length
    : 0;
  const charCount = localValue.length;

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden border border-slate-200 ring-1 ring-black/5 transition-all duration-300 ${
          isFullscreen
            ? "w-[98vw] h-[98vh]"
            : "w-[90vw] max-w-[800px] h-[70vh] max-h-[600px]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-violet-50 to-purple-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg shadow-sm text-white">
              <Sparkles size={18} />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800 tracking-tight">
                {title}
              </h2>
              <p className="text-xs text-slate-500">{description}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg transition-colors"
              title="Copy to clipboard"
            >
              {copied ? (
                <Check size={16} className="text-green-500" />
              ) : (
                <Copy size={16} />
              )}
            </button>
            <button
              onClick={handleReset}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg transition-colors"
              title="Reset changes"
            >
              <RotateCcw size={16} />
            </button>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg transition-colors"
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>

            <div className="h-6 w-px bg-slate-200 mx-1" />

            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:bg-red-50 hover:text-red-500 rounded-lg transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-hidden bg-white relative">
            <textarea
              ref={textareaRef}
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="w-full h-full p-5 font-mono text-sm bg-transparent text-slate-700 resize-none focus:outline-none selection:bg-violet-200 leading-relaxed placeholder:text-slate-400"
              spellCheck={false}
            />
          </div>

          {/* Status Bar */}
          <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 flex-shrink-0">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <Type size={12} />
                {charCount} characters
              </span>
              <span>{wordCount} words</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-[10px] font-mono">
                Ctrl
              </kbd>
              <span>+</span>
              <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-[10px] font-mono">
                Enter
              </kbd>
              <span className="text-slate-400">to save</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-100 bg-white flex justify-between items-center flex-shrink-0">
          <div className="text-xs text-slate-400">
            Press{" "}
            <kbd className="px-1 py-0.5 bg-slate-100 rounded text-[10px] font-mono">
              Esc
            </kbd>{" "}
            to close
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:bg-slate-50 hover:text-slate-800 rounded-lg font-medium text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-lg shadow-md hover:shadow-violet-500/25 font-semibold text-sm transition-all transform active:scale-95 flex items-center gap-2"
            >
              <Check size={16} />
              Save Prompt
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
